import React, { useState, useEffect, useCallback } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import {
  FeatureSection,
  Container,
  Header,
  HeaderTitle,
  HeaderSubtitle,
  HeaderDescription,
  CarouselWrapper,
  Slide,
  SlideImage,
  Grid,
  AmenityCard,
  StatsSection,
  StatBox,
  GallerySection,
  SectionTitle,
  GalleryGrid,
  GalleryItem,
  InfoSection,
  InfoCard,
  ContactCard,
  ButtonRow,
  CallButton,
  MapButton,
  LightboxOverlay,
  LightboxContent,
  LightboxImage,
  LightboxClose,
  LightboxNav,
  OfferBanner,
  OfferBadge,
  OfferTitle,
  OfferPrice,
  OfferDescription,
  BookOnlineButton,
} from "./PlayzoneFeature.styles";
import playzoneImages, { playzoneThumbnails } from "../../data/playzone_images";
import { MINGGO_MAP_URL } from "../../constants/maps";

const amenities = [
  {
    title: "Multi-Level Soft Play",
    description: "Features secure tunnels, foam obstacle courses, bridge paths, and safe climbing zones tailored to trigger active movement.",
  },
  {
    title: "Giant Ball Pool & Slides",
    description: "Over thousands of colorful, sanitized soft balls paired with double-wave slides for the ultimate slide-and-splash joy.",
  },
  {
    title: "Kids Birthday & Events",
    description: "A private, thematic party space with state-of-the-art music systems and tailored packages to make birthdays unforgettable.",
  },
  {
    title: "Trampoline Zone",
    description: "Fully padded trampoline zone offering kids a high-energy, safe bouncing environment to keep them active.",
  },
  {
    title: "Interactive Sensory Toys",
    description: "Creative blocks, cognitive puzzles, and learning sets that stimulate motor skills and mental growth in toddlers.",
  },
  {
    title: "Cafeteria & Parent Lounge",
    description: "A comfortable, air-conditioned waiting space for parents offering drinks, snacks, and a full view of the play arena.",
  },
];

const PlayzoneFeature = () => {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const openLightbox = (index) => {
    setLightboxIndex(index);
    document.body.style.overflow = "hidden";
  };

  const closeLightbox = useCallback(() => {
    setLightboxIndex(null);
    document.body.style.overflow = "";
  }, []);

  const navigateLightbox = useCallback((direction) => {
    setLightboxIndex((prevIndex) => {
      if (prevIndex === null) return null;
      if (direction === "next") {
        return (prevIndex + 1) % playzoneImages.length;
      } else {
        return (prevIndex - 1 + playzoneImages.length) % playzoneImages.length;
      }
    });
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (lightboxIndex === null) return;
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") navigateLightbox("next");
      if (e.key === "ArrowLeft") navigateLightbox("prev");
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [lightboxIndex, closeLightbox, navigateLightbox]);

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 600,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    arrows: true,
    adaptiveHeight: true,
    lazyLoad: "ondemand",
  };

  // We can use the first 5 images for the hero slider
  const heroSlides = playzoneImages.slice(0, 5);

  return (
    <FeatureSection id="playzone-store">
      <Container>
        <Header>
          <HeaderTitle>MinGGo Playzone</HeaderTitle>
          <HeaderSubtitle>Premium Kids' Indoor Play Area</HeaderSubtitle>
          <HeaderDescription>
            Explore a world of active fun, excitement, and memory-making. MinGGo Playzone offers an air-conditioned, clean, and fully-padded playground for toddlers and kids up to 10 years old. Designed under stringent safety standards, it is Nagpur's top destination for active recreation and spectacular birthday parties!
          </HeaderDescription>
        </Header>

        <CarouselWrapper>
          <Slider {...sliderSettings}>
            {heroSlides.map((src, index) => (
              <Slide key={index}>
                <SlideImage src={src} alt={`MinGGo Playzone highlight ${index + 1}`} />
              </Slide>
            ))}
          </Slider>
        </CarouselWrapper>

        <StatsSection>
          <StatBox>
            <h4>100% Padded</h4>
            <span>Safe & Inspected</span>
            <p>Complete soft-padding coverage and safety nets around all play structures.</p>
          </StatBox>
          <StatBox>
            <h4>Sanitized Daily</h4>
            <span>Ultra Hygienic</span>
            <p>Every toy, slide, and ball pit undergoes periodic deep cleaning cycles.</p>
          </StatBox>
          <StatBox>
            <h4>Private Party</h4>
            <span>Exclusive Venue</span>
            <p>Private bookings available for awesome, customized birthday parties.</p>
          </StatBox>
        </StatsSection>

        <Grid>
          {amenities.map((item) => (
            <AmenityCard key={item.title}>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </AmenityCard>
          ))}
        </Grid>

        <GallerySection>
          <SectionTitle>
            Inside the <span>Playzone</span>
          </SectionTitle>
          <GalleryGrid>
            {playzoneImages.map((src, index) => (
              <GalleryItem key={index} onClick={() => openLightbox(index)}>
                <img
                  src={playzoneThumbnails[index]}
                  alt={`MinGGo Playzone interior thumbnail ${index + 1}`}
                  loading="lazy"
                />
              </GalleryItem>
            ))}
          </GalleryGrid>
        </GallerySection>

        <InfoSection>
          <InfoCard>
            <h4>Rules & Policies</h4>
            <ul>
              <li><strong>Socks are Mandatory:</strong> For hygiene and grip, socks must be worn by children and accompanying parents inside the play zone.</li>
              <li><strong>Parental Supervision:</strong> Toddlers under 3 years must be accompanied and supervised by a parent or guardian at all times.</li>
              <li><strong>No Outside Food:</strong> Outside snacks and meals are prohibited inside the active playing arena (refreshments are available at the cafeteria).</li>
              <li><strong>Health & Hygiene:</strong> Children showing symptoms of fever, cold, or infections will not be admitted for the safety of others.</li>
            </ul>
          </InfoCard>

          <ContactCard>
            <h4>Timings & Bookings</h4>
            <div className="details">
              <div className="item">
                <strong>Opening Hours:</strong>
                <p style={{ margin: "4px 0 0 0" }}>Daily: 12:00 PM – 9:00 PM</p>
              </div>
              <div className="item">
                <strong>Playzone Location:</strong>
                <p style={{ margin: "4px 0 0 0" }}>
                  46, Gandhi Grain Market, Telephone Exchange Square, Central Avenue, Nagpur - 440008
                </p>
              </div>
              <div className="item">
                <strong>Inquiries & Party Bookings:</strong>
                <p style={{ margin: "4px 0 0 0" }}>Call: 8888865630 / 8999181685</p>
              </div>
            </div>

            <ButtonRow>
              <CallButton href="tel:8888865630">Call to Book</CallButton>
              <MapButton href={MINGGO_MAP_URL} target="_blank" rel="noopener noreferrer">
                View on Google Maps
              </MapButton>
            </ButtonRow>
          </ContactCard>
        </InfoSection>

        <OfferBanner>
          <OfferBadge>Happy Hour Special</OfferBadge>
          <OfferTitle>Enjoy Premium Playtime for Less!</OfferTitle>
          <OfferPrice>₹150 <span>/ hour</span></OfferPrice>
          <OfferDescription>
            Bring your little ones to MinGGo Playzone during happy hours and let them enjoy all the fun and activities at a special discounted rate of just ₹150 per hour.
          </OfferDescription>
          <BookOnlineButton href="https://minggo.in/" target="_blank" rel="noopener noreferrer">
            Book Online
          </BookOnlineButton>
        </OfferBanner>

        {lightboxIndex !== null && (
          <LightboxOverlay onClick={closeLightbox}>
            <LightboxContent onClick={(e) => e.stopPropagation()}>
              <LightboxClose onClick={closeLightbox} aria-label="Close photo preview">
                &times;
              </LightboxClose>
              <LightboxNav
                direction="prev"
                onClick={() => navigateLightbox("prev")}
                aria-label="Previous photo"
              >
                &#10094;
              </LightboxNav>
              <LightboxImage
                src={playzoneImages[lightboxIndex]}
                alt={`Full preview of playzone photo ${lightboxIndex + 1}`}
              />
              <LightboxNav
                direction="next"
                onClick={() => navigateLightbox("next")}
                aria-label="Next photo"
              >
                &#10095;
              </LightboxNav>
            </LightboxContent>
          </LightboxOverlay>
        )}
      </Container>
    </FeatureSection>
  );
};

export default PlayzoneFeature;
